import type { PhaseKey } from "@akina/domain";
import type { Tenant, AppLocale } from "@akina/infra";
import type { TherapistFeedbackTypeEnum } from "types/schemas";
import type { ApiConfigurationCreation } from "..";

export const apiEndpoints = (config: ApiConfigurationCreation) => ({
  auth: {
    changePassword: `${config.baseUrl}/security/change-password/`,
    resendVerification: `${config.baseUrl}/security/send-verification-email/`,
  },
  library: `${config.baseUrl}/library/`,
  patients: {
    get: `${config.baseUrl}/patients/information/`,
    emailAlreadyExists: (email: string, tenant: Tenant) => `${config.baseUrl}/users/${email}/exists/?tenant=${tenant}`,
    update: `${config.baseUrl}/patients/information/`,
    profilePicture: {
      uploadUrl: `${config.baseUrl}/patients/profile-picture/upload-url/`,
      delete: `${config.baseUrl}/users/profile-picture/`,
    },
    getTermsAndConditions: `${config.baseUrl}/patients/terms-accepted-status/`,
    acceptTermsAndConditions: `${config.baseUrl}/patients/accept-terms-and-conditions/`,
  },
  therapists: {
    profilePicture: {
      uploadUrl: `${config.baseUrl}/therapists/profile-picture/upload-url/`,
      delete: `${config.baseUrl}/users/profile-picture/`,
    },
    devices: {
      list: `${config.baseUrl}/therapists/devices/`,
      register: `${config.baseUrl}/therapists/devices/register/`,
      unregister: `${config.baseUrl}/therapists/devices/unregister/`,
    },
    get: `${config.baseUrl}/therapists/`,
    myTherapist: `${config.baseUrl}/therapists/information/`,
    invitePatient: `${config.baseUrl}/therapists/invite-patient/`,
    getTermsAndConditions: (id: string) => `${config.baseUrl}/therapists/${id}/terms-accepted-status/`,
    acceptTermsAndConditions: `${config.baseUrl}/therapists/accept-terms-and-conditions/`,
    delete: (therapistId: string) => `${config.baseUrl}/therapists/${therapistId}/`,
    editRole: `${config.baseUrl}/therapists/edit-role/`,
    sendReminder: `${config.baseUrl}/therapists/send-reminder/`,
    resendInvite: `${config.baseUrl}/therapists/resend-invite/`,
    revokeInvite: `${config.baseUrl}/therapists/revoke-invite/`,
    happinessFeedback: `${config.baseUrl}/therapists/happiness-feedback/`,
    getFavouriteExercises: `${config.baseUrl}/therapists/favourite-exercises/`,
    displayHappinessQuestionnaire: (therapistId: string) =>
      `${config.baseUrl}/therapists/${therapistId}/happiness-questionnaire/`,
    getTreatmentTemplates: (therapistId: string) => `${config.baseUrl}/therapists/${therapistId}/treatment-templates/`,
    getTreatmentTemplateWithPersonalizations: (therapistId: string, treatmentTemplateKey: string) =>
      `${config.baseUrl}/therapists/${therapistId}/treatment-templates/${treatmentTemplateKey}/`,
  },
  clinics: {
    profilePicture: {
      uploadUrl: `${config.baseUrl}/organizations/profile-picture/upload-url/`,
      delete: `${config.baseUrl}/organizations/profile-picture/`,
    },
    images: {
      uploadUrl: `${config.baseUrl}/organizations/images/upload-url/`,
      delete: (imageUrl: string) => `${config.baseUrl}/organizations/images/?image_url=${imageUrl}`,
    },
    get: (organizationId: string) => `${config.baseUrl}/organizations/${organizationId}/information/`,
    getImages: (organizationId: string) => `${config.baseUrl}/organizations/${organizationId}/images/`,
    update: `${config.baseUrl}/organizations/information/`,
    inviteTherapist: `${config.baseUrl}/organizations/invite-therapist/`,
    getTreatments: (organizationId: string) => `${config.baseUrl}/organizations/${organizationId}/treatment-logs/`,
    getTherapists: (organizationId: string) => `${config.baseUrl}/organizations/${organizationId}/therapists/`,
    getSupportContacts: (organizationId?: string) =>
      organizationId
        ? `${config.baseUrl}/organizations/support-contacts/?organization_id=${organizationId}`
        : `${config.baseUrl}/organizations/support-contacts/`,
    insights: (organizationId: string) => `${config.baseUrl}/organizations/${organizationId}/insights/`,
  },
  onboarding: `${config.baseUrl}/patients/onboard/`,
  onboardingStatus: (onboardingId: string) => `${config.baseUrl}/organizations/onboarding/${onboardingId}/`,
  treatments: {
    editGoal: `${config.baseUrl}/treatments/edit-goal/`,
    editDiagnosis: `${config.baseUrl}/treatments/diagnosis/`,
    updatePhase: `${config.baseUrl}/treatments/active-phase/`,
    disconnect: `${config.baseUrl}/treatments/disconnect/`,
    updateTreatmentClinicTherapist: `${config.baseUrl}/treatments/assign-organization-therapist/`,
    updateTreatmentTherapist: `${config.baseUrl}/treatments/assign-therapist/`,
    all: `${config.baseUrl}/treatments/`,
    getActive: `${config.baseUrl}/treatments/active/`,
    uploadPrescription: (id: string, fileType: string) =>
      `${config.baseUrl}/treatments/${id}/prescription/upload-url/?file_type=${encodeURIComponent(fileType)}`,
    prescriptionUploadSuccess: `${config.baseUrl}/treatments/prescription/upload-success/`,
    getPrescriptionUrls: (id: string) => `${config.baseUrl}/treatments/${id}/prescription/`,
    progressData: (treatmentId: string) => `${config.baseUrl}/treatments/${treatmentId}/weekly-progress/`,
    happinessFeedback: (treatmentId: string) => `${config.baseUrl}/treatments/${treatmentId}/happiness-feedback/`,
    displayHappinessQuestionnaire: (treatmentId: string) =>
      `${config.baseUrl}/treatments/${treatmentId}/happiness-questionnaire/`,
    extendTreatment: `${config.baseUrl}/treatments/extend-treatment/`,
    exerciseInsights: (treatmentId: string) => `${config.baseUrl}/treatments/${treatmentId}/exercises-insights/`,
    tasksSummaries: (therapistId: string) => `${config.baseUrl}/treatments/${therapistId}/tasks-summaries/`,
    getPlannedTreatment: (treatmentTemplateKey: string) =>
      `${config.baseUrl}/treatments/${treatmentTemplateKey}/planned-treatments/`,

    updatePlannedTreatment: `${config.baseUrl}/treatments/planned-treatments/`,
    updatePlannedRoutine: `${config.baseUrl}/treatments/planned-routines/`,
    updatePlannedSession: `${config.baseUrl}/treatments/planned-sessions/`,

    resetPlannedTreatment: `${config.baseUrl}/treatments/planned-treatments/reset/`,
    resetPlannedSession: `${config.baseUrl}/treatments/planned-sessions/reset/`,
    resetPlannedRoutine: `${config.baseUrl}/treatments/planned-routines/reset/`,

    // Personalizations ---
    getPersonalizations: `${config.baseUrl}/treatments/personalizations/`,
    createPersonalization: `${config.baseUrl}/treatments/personalization/`,
    applyPersonalization: `${config.baseUrl}/treatments/personalization/apply/`,
    updatePersonalizationSessionPlan: `${config.baseUrl}/treatments/personalization/planned-sessions/`,
    getPersonalization: (id: string) => `${config.baseUrl}/treatments/personalizations/${id}/`,
    updatePersonalization: (id: string) => `${config.baseUrl}/treatments/personalization/${id}/`,
    deletePersonalization: (id: string) => `${config.baseUrl}/treatments/personalization/${id}/`,

    getExerciseSummaries: (locale: string) => `${config.baseUrl}/templates/exercises/?language=${locale}`,
    getExerciseTemplate: (
      treatmentTemplateKey: string,
      exerciseTemplateKey: string,
      phaseKey: string,
      locale: AppLocale,
    ) =>
      `${config.baseUrl}/templates/exercises/${treatmentTemplateKey}/${exerciseTemplateKey}/${phaseKey}/?language=${locale}`,
  },
  difficultyOptions: {
    get: (params: {
      treatmentTemplateKey: string | null;
      routineTemplateKey: string | null;
      phaseKey: PhaseKey | null;
    }) =>
      `${config.baseUrl}/templates/difficulty-options/${params.treatmentTemplateKey}/${params.routineTemplateKey}/${params.phaseKey}/`,
  },
  therapySessions: {
    feedback: {
      get: (treatmentId: string, reviewedOnly: boolean = false) =>
        `${config.baseUrl}/treatments/${treatmentId}/feedback/?reviewed_only=${reviewedOnly}`,
      viewed: `${config.baseUrl}/sessions/feedback/viewed/`,
      react: `${config.baseUrl}/sessions/feedback/react/`,
      reactFromEmail: `${config.baseUrl}/sessions/feedback/reaction/from-email/`,
    },
    startSession: `${config.baseUrl}/sessions/start/`,
    endSession: `${config.baseUrl}/sessions/end/`,
    cancelSession: `${config.baseUrl}/sessions/cancel/`,
    cancelRoutine: `${config.baseUrl}/sessions/routines/cancel/`,
    updateRoutineProgress: `${config.baseUrl}/sessions/routines/progress/`,
    updateSelfReflection: `${config.baseUrl}/sessions/finalize/`,
    nextSession: (treatmentId: string, params: { includeMsps: boolean; onlyScriptGeneration: boolean }) =>
      `${config.baseUrl}/treatments/${treatmentId}/next-session/?include_msps=${params.includeMsps}&only_script_generation=${params.onlyScriptGeneration}`,
    sessionRecap: (sessionId: string) => `${config.baseUrl}/sessions/${sessionId}/recap/`,
    sessionStatus: (sessionId: string) => `${config.baseUrl}/sessions/${sessionId}/status/`,
    musicSources: (stationId: string) => `${config.baseUrl}/media-streaming/music/?station=${stationId}`,
  },
  therapistNotes: `${config.baseUrl}/therapists/notes/`,
  sessionReview: {
    reviewed: `${config.baseUrl}/sessions/reviewed/`,
    treatmentSummary: (treatmentId: string) => `${config.baseUrl}/treatments/${treatmentId}/summary/`,
    routineDifficulties: (sessionId: string, routineId: number) =>
      `${config.baseUrl}/sessions/${sessionId}/routines/${routineId}/difficulty/`,
    mediaStorage: (sessionId: string, feedbackType: TherapistFeedbackTypeEnum | null | undefined, routineId?: number) =>
      `${config.baseUrl}/sessions/${sessionId}/media-storage/?routine_log_id=${
        routineId ?? 0
      }&feedback_type=${feedbackType}`,
    sessionFeedback: `${config.baseUrl}/sessions/feedback/`,
    routineFeedback: {
      base: `${config.baseUrl}/sessions/routines/feedback/`,
      react: `${config.baseUrl}/sessions/routines/feedback/react/`,
    },
    reviewTask: (sessionId: string) => `${config.baseUrl}/sessions/${sessionId}/review-task/`,
    feedbacksSummary: (therapistId: string, treatmentId?: string) => {
      if (treatmentId)
        return `${config.baseUrl}/sessions/${therapistId}/feedbacks-summary/?treatment_id=${treatmentId}`;
      return `${config.baseUrl}/sessions/${therapistId}/feedbacks-summary/`;
    },
  },
});
